import React, { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import "../styles/ConfidenceStep.css";
import { toast } from "react-toastify";

const ConfidenceStep = ({
  userPicks,
  onClose,
  currentUserId,
  onSaveAndClose,
  currentSeason,
  onBack,
}) => {
  const [orderedPicks, setOrderedPicks] = useState([]);
  const [highlighted, setHighlighted] = useState(false);

  // ✅ Use environment variable for API base URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || "";

  useEffect(() => {
    const picksArray = Object.entries(userPicks).map(([gameId, teamData]) => ({
      gameId,
      ...teamData,
    }));
    setOrderedPicks(picksArray);
  }, [userPicks]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const updatedPicks = [...orderedPicks];
    const [movedItem] = updatedPicks.splice(result.source.index, 1);
    updatedPicks.splice(result.destination.index, 0, movedItem);
    setOrderedPicks(updatedPicks);
  };



  
  const handleSave = async () => {
    if (orderedPicks.length === 0) {
      alert("Please reorder your picks before submitting.");
      return;
    }

    const picks = orderedPicks.map((pick, index) => ({
      userId: currentUserId,
      gameId: pick.gameId,
      selectedWinner: pick.selectedTeam,
      confidence: 48 - index,
      season: currentSeason,
    }));

    console.log("User Picks:", picks);

    try {
      const response = await fetch(`${API_BASE_URL}/api/picks/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ picks }),
        credentials: "include",
      });

      if (response.ok) {
        console.log("Picks submitted successfully");
        toast.success("Picks submitted successfully!");
        onSaveAndClose();
        onClose();
      } else {
        const errorData = await response.json();
        console.error("Error saving picks:", errorData.message);
        toast.error(`Error saving picks: ${errorData.message}`);
      }
    } catch (error) {
      console.error("Error saving picks:", error);
      toast.error("An unexpected error occurred while saving picks.");
    }
  };

const handleAutoRank = () => {
  if (!orderedPicks || orderedPicks.length === 0) return;

  const sorted = [...orderedPicks].sort((a, b) => {
    // Check if spread exists
    const aHasSpread = a.spread !== undefined && a.spread !== null && a.spread !== "";
    const bHasSpread = b.spread !== undefined && b.spread !== null && b.spread !== "";

    // Push picks with no spread to the bottom
    if (!aHasSpread && bHasSpread) return 1;
    if (aHasSpread && !bHasSpread) return -1;
    if (!aHasSpread && !bHasSpread) return 0;

    // Parse spread as numeric absolute value
    const aSpread = Math.abs(parseFloat(a.spread));
    const bSpread = Math.abs(parseFloat(b.spread));

    // Check if user picked the favorite
    const aPickedFavorite = a.favorite && a.selectedTeam === a.favorite;
    const bPickedFavorite = b.favorite && b.selectedTeam === b.favorite;

    // Favorites above underdogs
    if (aPickedFavorite && !bPickedFavorite) return -1;
    if (!aPickedFavorite && bPickedFavorite) return 1;

    // Within favorites: higher spread = higher confidence
    if (aPickedFavorite && bPickedFavorite) return bSpread - aSpread;

    // Within underdogs: higher spread = lower confidence
    if (!aPickedFavorite && !bPickedFavorite) return aSpread - bSpread;

    return 0;
  });

  console.table(
    sorted.map((p) => ({
      Game: p.gameName,
      Favorite: p.favorite || "N/A",
      Spread: p.spread || "N/A",
      Picked: p.selectedTeam,
      "Picked Favorite?": p.favorite && p.selectedTeam === p.favorite ? "✅" : "❌",
    }))
  );

  setOrderedPicks(sorted);
  toast.info("Picks auto-ranked by spread and pick type!");
};





  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="confidence-step-container">
        
        <h2>Step 2: Place your Bets!</h2>
        <p>
          Now you have the opportunity to wager bets on each pick. Use the drag
          and drop feature to reorder your picks so that you are wagering the
          most points on the picks you are most confident in or use the Auto-Rank button!
        </p>

        <button className="auto-rank-button" onClick={handleAutoRank}>
          ⚡ Auto-Rank Picks
        </button>

        <Droppable droppableId="picks-list">
          {(provided) => (
            <div
              className={`picks-container ${
                highlighted ? "auto-rank-highlight" : ""
              }`}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {orderedPicks.map((pick, index) => (
                <Draggable
                  key={pick.gameId}
                  draggableId={`${pick.gameId}-${index}`}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <div
                      className={`pick-card ${
                        snapshot.isDragging ? "dragging" : ""
                      }`}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <div className="confidence-section">
                        <div className="confidence-points">
                          <div className="points">{48 - index}</div>
                          <div className="label">Points</div>
                        </div>
                      </div>
                      <div className="pick-details">
                        <div className="game-info">
                          <div className="game-name">
                            <strong>{pick.gameName}</strong>
                          </div>
                        </div>
                        <div className="spread">
                          {pick.matchup}, {pick.fabbrev} {pick.spread}
                        </div>
                        <div className="selected-team">
                          Pick: <i>{pick.selectedTeam}</i>
                        </div>
                      </div>
                      <div className="gripper">
                        <span></span>
                        <span></span>
                        <span></span>
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>

      <div className="finalize-button-container">
        
        <button className="save-button" onClick={handleSave}>
          Save and Close
        </button>
        <button className="modal-close-button" onClick={onClose}>
          Close
        </button>
      </div>
    </DragDropContext>
  );
};

export default ConfidenceStep;
